import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { updateRole } from '../../services/roleService';
import { getAllTalentProfiles } from '../../services/talentService';

import VoiceTypeSlider from './VoiceTypeSlider';
import AgeRangeSlider from './AgeRangeSlider';
import LoadingSpinner from './LoadingSpinner';
import Icon from '../AppIcon';


const EditRoleModal = ({ isOpen, onClose, role, onSuccess }) => {
  const [formData, setFormData] = useState({
    character_name: '',
    voice_type: '',
    age_range_min: 18,
    age_range_max: 65,
    vocal_range: 'C3-C5',  // Changed: single vocal_range field matching database
    description: '',
    assigned_talent_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [talentProfiles, setTalentProfiles] = useState([]);
  const [loadingTalent, setLoadingTalent] = useState(false);

  // Fetch talent profiles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTalentProfiles();
    }
  }, [isOpen]);

  const fetchTalentProfiles = async () => {
    setLoadingTalent(true);
    try {
      const { data: profiles } = await getAllTalentProfiles();
      setTalentProfiles(profiles || []);
    } catch (err) {
      console.error('Error fetching talent profiles:', err);
      setError('Failed to load talent profiles');
      setTalentProfiles([]);
    } finally {
      setLoadingTalent(false);
    }
  };

  useEffect(() => {
    if (role) {
      // Parse age_range from "min-max" format to separate min/max values
      let ageMin = 18;
      let ageMax = 65;
      if (role?.age_range && typeof role?.age_range === 'string') {
        const parts = role?.age_range?.split('-');
        if (parts?.length === 2) {
          ageMin = parseInt(parts?.[0]) || 18;
          ageMax = parseInt(parts?.[1]) || 65;
        }
      }

      setFormData({
        character_name: role?.character_name || '',
        voice_type: role?.voice_type || '',
        age_range_min: ageMin,
        age_range_max: ageMax,
        vocal_range: role?.vocal_range || 'C3-C5',  // Changed: use single vocal_range field
        description: role?.description || '',
        assigned_talent_id: role?.assigned_talent_id || null,
      });
    }
  }, [role]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Combine age_range_min and age_range_max back into "min-max" format
      const ageRange = `${formData?.age_range_min}-${formData?.age_range_max}`;
      
      const updateData = {
        character_name: formData?.character_name,
        voice_type: formData?.voice_type,
        age_range: ageRange,
        vocal_range: formData?.vocal_range || null,  // Changed: only send existing field
        description: formData?.description || null,
        assigned_talent_id: formData?.assigned_talent_id || null,
      };

      await updateRole(role?.id, updateData);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err?.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white border-opacity-20">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white border-opacity-20">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Icon name="Edit3" size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-semibold text-white">Edit Role</h2>
                  <p className="text-sm text-blue-200 font-caption">Edit {role?.character_name} details and manage song assignments</p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={loading}
                className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-blue-200 hover:text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-500 bg-opacity-20 border border-red-400 text-red-100 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Character Name */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2 font-caption">
                  Character Name *
                </label>
                <input
                  type="text"
                  value={formData?.character_name}
                  onChange={(e) => setFormData({ ...formData, character_name: e?.target?.value })}
                  placeholder="Enter character name"
                  required
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Voice Type */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2 font-caption">
                  Voice Type
                </label>
                <VoiceTypeSlider
                  value={formData?.voice_type}
                  onChange={(value) => setFormData({ ...formData, voice_type: value })}
                  disabled={loading}
                />
              </div>

              {/* Age Range */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2 font-caption">
                  Age Range
                </label>
                <AgeRangeSlider
                  min={formData?.age_range_min}
                  max={formData?.age_range_max}
                  onChange={(min, max) => setFormData({ ...formData, age_range_min: min, age_range_max: max })}
                  disabled={loading}
                />
              </div>

              {/* Vocal Range */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2 font-caption">
                  Vocal Range
                </label>
                <input
                  type="text"
                  value={formData?.vocal_range}
                  onChange={(e) => setFormData({ ...formData, vocal_range: e?.target?.value })}
                  placeholder="e.g. C3-C5, E2-F5"
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                />
                <p className="text-xs text-blue-300 mt-1 font-caption">
                  Enter vocal range in format: Low-High (e.g., C3-C5)
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2 font-caption">
                  Character Description
                </label>
                <textarea
                  value={formData?.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Enter character description (optional)"
                  rows={4}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 resize-none"
                  disabled={loading}
                />
              </div>

              {/* Talent Assignment */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2 font-caption">
                  Assign Talent Member
                </label>
                {loadingTalent ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <select
                    value={formData?.assigned_talent_id || ''}
                    onChange={(e) => setFormData({ ...formData, assigned_talent_id: e?.target?.value || null })}
                    className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="" className="bg-gray-800 text-white">-- No Talent Assigned --</option>
                    {talentProfiles?.map((talent) => (
                      <option key={talent?.id} value={talent?.id} className="bg-gray-800 text-white">
                        {talent?.display_name || talent?.stage_name || 'Unnamed Talent'}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-blue-300 mt-1 font-caption">
                  Select a talent member to assign to this role (optional)
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white border-opacity-20">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 text-blue-100 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-all duration-200 font-caption font-medium"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-caption font-medium"
                  disabled={loading}
                >
                  {loading && <LoadingSpinner size="sm" />}
                  {loading ? 'Updating...' : 'Update Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default EditRoleModal;